import * as z from 'zod/v4';

/**
 * Creates a new date in correct format.
 *
 * @param dateLike Date to create from
 * @returns formatted JSON date
 */
export function createDateTime(dateLike: Date | string | number = new Date()): DateTime {
  return new Date(dateLike).toISOString() as DateTime;
}

/**
 * Zod schema for DateTime
 */
export const dateTimeSchema = z
  .string()
  .refine((val) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:?\d{2}|Z)$/.test(val), {
    message: 'Date string must be in ISO 8601 format and include offset',
  })
  .refine((val) => !Number.isNaN(Date.parse(val)), {
    message: 'Invalid date string',
  })
  .transform((d) => new Date(d).toISOString())
  .brand<'DateTime'>();
export type DateTime = z.infer<typeof dateTimeSchema>;

const isoStringFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;

/**
 * TS type guard function for DateTime.
 *
 * @param value value to check is a valid DateTime
 * @returns true if value is a valid DateTime
 */
export function isDateTime(value: string): value is DateTime {
  const date = new Date(value);

  if (!(date instanceof Date)) {
    return false;
  }

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return isoStringFormat.test(value);
}

/**
 * Cast an optional date to a DateTime type (Date object).
 *
 * @param date Optional date to cast to DateTime
 * @returns DateTime as a branded type
 */
export function getDateTime(date?: Date): DateTime;

/**
 * Cast a date to a branded DateTime type (Date object).
 *
 * @param value Optional date instance. If not provided, the current date is used.
 * @returns DateTime as a branded type
 * @throws {Error} if the date is invalid DateTime
 */
export function getDateTime(value?: Date): DateTime {
  const theDate = value ?? new Date();
  const dateTime = theDate.toISOString();

  if (!isDateTime(dateTime)) {
    throw new Error('Invalid date time');
  }

  return dateTime;
}
